import { createHash } from 'crypto';

import { NEXT_PUBLIC_GTM_ID } from '@env';

/**
 * Google Tag Manager utilities.
 *
 * Testing note: prefer not to much any of these methods, except `loadGtm()`.
 * All side effects are contained in `window.gtag()`.
 */

export class GoogleTagManagerUtils {
  /**
   * Pushes an event to GTM's dataLayer. Loads GTM if not already loaded.
   * This is equivalent to calling `gtag(args)` or `dataLayer.push(args)`.
   */

  static push<Command extends keyof Gtag.GtagCommands>(
    command: Command,
    ...rest: Gtag.GtagCommands[Command]
  ): void {
    GoogleTagManagerUtils.loadGtm();
    console.debug('GoogleTagManagerUtils: push', [command, ...rest]);
    window?.gtag?.(command, ...rest);
  }

  /**
   * Injects the GTM script and initialises dataLayer/gtag. Idempotent.
   *
   * This differs from @next/third-parties@15.5.4 in a few ways:
   *
   * - Having to call loadGtm() manually makes it NOT load across all pages.
   *   This is itended to reduce noise in Google Ads console.
   *
   * - It sets `window.gtag()` as suggested by Google.
   *
   * - It does not require 'unsafe-inline' in the CSP policy or a nonce.
   *
   * - It loads gtag via gtag/js instead of gtm.js.
   */

  static loadGtm(options: { gtmId?: string | null | undefined } = {}): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const gtmId = options.gtmId ?? NEXT_PUBLIC_GTM_ID;
    if (!gtmId) {
      return;
    }

    // Only load it once
    if (document.getElementById('gtm-script')) {
      return;
    }

    console.debug('GoogleTagManagerUtils: loading GTM');

    // Inject GTM script
    const script = document.createElement('script');
    script.async = true;
    script.id = 'gtm-script';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gtmId}`;
    document.body.appendChild(script);

    // Initialise dataLayer and gtag. This is script provided by Google
    // @ts-expect-error ...
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        // @ts-expect-error ...
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', gtmId);
    }
  }

  static normalizeEmail(email: string): string {
    /*
     * Google Ads requires lowercase and trim on email addresses.
     * See: https://github.com/BlinqHQ/data-warehouse/blob/4120e4c53c582b32851d74e7834c0ad6bee3b7ad/definitions/dwh_outputs/growth/output_conversion_events_google_ads.sqlx#L36
     */
    const trimmed = email.trim().toLowerCase();
    const [localPart, domain] = trimmed.split('@');

    if (!domain || !localPart) {
      return trimmed;
    }

    /*
     * Google Ads requires removal of any '.' characters preceding "gmail.com" or "googlemail.com".
     * See: https://github.com/BlinqHQ/data-warehouse/blob/b2c5d9ab6e1bff80e44501ab0c6d1227ee2e325c/platform/orchestration/pipelines/library/integrations/google/ads.py#L62-L85
     */
    if (domain === 'gmail.com' || domain === 'googlemail.com') {
      return `${localPart.replace(/\./g, '')}@${domain}`;
    }

    return trimmed;
  }

  static hashEmail(email: string): string {
    const normalized = GoogleTagManagerUtils.normalizeEmail(email);
    return createHash('sha256').update(normalized).digest('hex');
  }

  /**
   * Send user data to Google Tag Manager for enhanced conversions.
   *
   * Only send the hashed emails - this is what's used for correlation later on.
   *
   * See:
   * - https://nextjs.org/docs/app/guides/third-party-libraries#sending-events
   * - https://support.google.com/google-ads/answer/13262500?#zippy=%2Cidentify-and-define-your-enhanced-conversions-variables
   */

  static setUserData(userData: { email: string | null | undefined }): void {
    if (!userData.email || userData.email.trim() === '') {
      return;
    }

    const hashedEmail = GoogleTagManagerUtils.hashEmail(userData.email);
    GoogleTagManagerUtils.push('set', 'user_data', { sha256_email_address: hashedEmail });
  }

  static sendFormSubmitEvent(): void {
    // 'form_submit' is not a documented GTM event. The event name was advised
    // by Google support. See: BIZ-598
    GoogleTagManagerUtils.push('event', 'form_submit', { send_to: NEXT_PUBLIC_GTM_ID });
  }
}
